import { useRef, useState } from 'react';
import noPhoto from '../../assets/shared/no-photo.jpg';
import { ApiError } from '../../lib/api';
import { mediaApi, mediaUrl } from '../../lib/mediaApi';

function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

/** A big, centered "hero" photo for the object card popup — built as a real
 * carousel (dots + arrows) so it's ready the day a feature can hold more than
 * one photo, but the data model only has a single photo_file_id today, so
 * with one photo it just renders that image with no carousel chrome. */
export function PhotoCarousel({
  photoId,
  canEdit,
  onChange,
  heightClassName = 'h-72',
}: {
  photoId: string | null | undefined;
  canEdit: boolean;
  onChange?: (fileId: string) => Promise<void> | void;
  heightClassName?: string;
}) {
  const photos = photoId ? [photoId] : [];
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const uploaded = await mediaApi.upload(file);
      await onChange?.(uploaded.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить фото.');
    } finally {
      setUploading(false);
    }
  };

  const current = photos[index] ?? null;

  return (
    <div className={`relative w-full overflow-hidden rounded-2xl bg-bg-surface-secondary ${heightClassName}`}>
      <img
        src={current ? mediaUrl(current) : noPhoto}
        alt=""
        onClick={() => current && setLightboxOpen(true)}
        className={`size-full object-cover ${current ? 'cursor-zoom-in' : ''}`}
      />

      {current && (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          title="Открыть на весь экран"
          className="absolute left-3 top-3 flex size-9 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
        >
          <ExpandIcon className="size-4" />
        </button>
      )}

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + photos.length) % photos.length)}
            className="absolute left-3 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % photos.length)}
            className="absolute right-3 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
          >
            ›
          </button>
          <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                className={`size-1.5 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}

      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            title="Загрузить фото"
            className="absolute right-3 top-3 flex h-9 items-center rounded-lg bg-black/40 px-3 text-xs font-medium text-white hover:bg-black/60 disabled:opacity-60"
          >
            {uploading ? '…' : '✎ Изменить фото'}
          </button>
        </>
      )}

      {error && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-status-danger px-2 py-1 text-[11px] font-medium text-text-inverse">
          {error}
        </span>
      )}

      {lightboxOpen && current && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            title="Закрыть"
            className="absolute right-5 top-5 flex size-10 items-center justify-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20"
          >
            ×
          </button>
          <img src={mediaUrl(current)} alt="" className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
